import { normalizePlainText } from './normalizePlainText';
import { effectivePlainTextBody } from '../utils/phoenixPlaceholderPlainText';

const BLOCK_TAGS = new Set(['p', 'div', 'pre', 'tr', 'li', 'h1', 'h2', 'h3', 'hr', 'blockquote', 'table']);

function extractTextWithNewlines(root: Element): string {
  const parts: string[] = [];

  const walk = (node: globalThis.Node) => {
    if (node.nodeType === globalThis.Node.TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== globalThis.Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'br') {
      parts.push('\n');
      return;
    }
    if (tag === 'pre') {
      parts.push(el.textContent ?? '');
      return;
    }
    for (const child of Array.from(el.childNodes)) {
      walk(child);
    }
    if (BLOCK_TAGS.has(tag)) {
      parts.push('\n');
    }
  };

  for (const child of Array.from(root.childNodes)) {
    walk(child);
  }
  return parts.join('').replace(/\r\n/g, '\n');
}

export type ExtractPlainTextFromHtmlOptions = {
  /** See {@link normalizePlainText} — default true. Use false for compose save/send. */
  reflowOpsTemplates?: boolean;
};

/** Convert HTML (including full .eml HTML documents) to plain text with line breaks preserved. */
export function extractPlainTextFromHtml(
  html: string,
  options?: ExtractPlainTextFromHtmlOptions,
): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  const normalizeOpts = { reflowOpsTemplates: options?.reflowOpsTemplates };
  if (typeof document === 'undefined') {
    return normalizePlainText(trimmed.replace(/<[^>]+>/g, ''), normalizeOpts);
  }
  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const root = doc.body;
  if (!root) {
    return normalizePlainText(trimmed.replace(/<[^>]+>/g, ''), normalizeOpts);
  }
  return normalizePlainText(extractTextWithNewlines(root), normalizeOpts);
}

function looksLikeHtmlMarkup(value: string): boolean {
  return /<(?:html|body|head|p|div|table|pre|br|span|font|strong|b|em|i|meta|!doctype)\b/i.test(value);
}

/** Prefer bodyText; otherwise extract plain text from HTML (never return raw HTML tags). */
export function plainBodyFromParts(
  bodyHtml: string | null | undefined,
  bodyText: string | null | undefined,
): string {
  const text = effectivePlainTextBody(bodyText);
  if (text) return normalizePlainText(text);

  const html = bodyHtml?.trim() ?? '';
  if (!html) return '';

  if (!looksLikeHtmlMarkup(html)) {
    return normalizePlainText(html);
  }

  return extractPlainTextFromHtml(html);
}
